// common/services/player/playerProfile.ts
import req from '../../api/request'
import { ENDPOINTS } from '../../api/endpoints'

export type PlayerProfileImage = {
  id?: string
  full_path?: string | null
  filename?: string | null
} & Record<string, unknown>

/**
 * Player profile fields surfaced on the Edit Player screen. Mirrors the
 * /users/me/family item shape the BE returns from GET/PUT /players/:id.
 */
export type PlayerProfileResponse = {
  id: string
  name_first?: string | null
  name_last?: string | null
  full_name?: string | null
  email?: string | null
  birth_date?: string | null
  /** Age in years, computed by the BE. Prefer this over deriving from birth_date. */
  age?: number | null
  /** Height in metres (stringified). UI converts to imperial for display. */
  height?: string | number | null
  weight?: number | null
  gender?: string | null
  gender_id?: number | null
  phone_number?: string | null
  alternate_phone_number?: string | null
  preferred_contact_method?: 'phone' | 'email' | null
  preferred_language?: string | null
  emergency_contact_name?: string | null
  emergency_contact_relationship?: string | null
  emergency_contact_phone_number?: string | null
  instagram?: string | null
  facebook?: string | null
  tiktok?: string | null
  youtube?: string | null
  league_registration_number?: string | null
  hockey_usa_number?: string | null
  hockey_canada_number?: string | null
  custom_membership_number?: string | null
  loyalty_number?: string | null
  /**
   * Canlan Salesforce ID. Read-only/display-only here — surfaced as a chip in
   * the Edit Player modal. Optional: not every tenant or player has one, and the
   * BE may omit the key entirely.
   */
  sf_id?: string | null
  address?:
    | ({
        id?: string
        street_1?: string | null
        street_2?: string | null
        city?: string | null
        postal_code?: string | null
        country?: { id?: number; name?: string } | null
        province?: { id?: number; name?: string } | null
      } & Record<string, unknown>)
    | null
  image?: PlayerProfileImage | null
} & Record<string, unknown>

export type UpdatePlayerPayload = Partial<{
  name_first: string
  name_last: string
  birth_date: string
  gender_id: number
  height: number
  weight: number
  email: string
  phone_number: string
  alternate_phone_number: string
  preferred_contact_method: 'phone' | 'email' | ''
  preferred_language: string
  emergency_contact_name: string
  emergency_contact_relationship: string
  emergency_contact_phone_number: string
  instagram: string
  facebook: string
  tiktok: string
  youtube: string
  league_registration_number: string
  hockey_usa_number: string
  hockey_canada_number: string
  custom_membership_number: string
  loyalty_number: string
  address: {
    street_1: string
    street_2: string
    city: string
    postal_code: string
    country_id: number | null
    province_id: number | null
  }
}>

/**
 * GET /players/:playerId — full player profile. Used by existing callers that
 * read the public player shape (e.g. the `teams` object). Unchanged.
 */
export const getPlayerProfile = async (
  playerId: string
): Promise<PlayerProfileResponse> => {
  const { data } = await req(ENDPOINTS.PLAYERS.GET_PLAYER(playerId))
  return data as PlayerProfileResponse
}

/** PUT /players/:playerId — partial update of a player's profile. Unchanged. */
export const updatePlayerProfile = async (
  playerId: string,
  payload: UpdatePlayerPayload
): Promise<PlayerProfileResponse> => {
  const { data } = await req(ENDPOINTS.PLAYERS.UPDATE_PLAYER(playerId), {
    method: 'PUT',
    // req() passes body straight to fetch (Content-Type: application/json) — it
    // does NOT stringify, so we must. Matches every other req() caller.
    body: JSON.stringify(payload),
  })
  return data as PlayerProfileResponse
}

// ---- Self-scoped (/users/me/players/:id) — used ONLY by the new Edit Player
// profile modal (web + mobile). Kept separate from the functions above so other
// callers of GET /players/:id (which read `teams` etc.) are unaffected. ----

/** GET /users/me/players/:playerId — profile for the new Edit Player modal. */
export const getMePlayerProfile = async (
  playerId: string
): Promise<PlayerProfileResponse> => {
  const { data } = await req(ENDPOINTS.PLAYERS.GET_ME_PLAYER(playerId))
  return data as PlayerProfileResponse
}

/** PUT /users/me/players/:playerId — partial update from the Edit Player modal. */
export const updateMePlayerProfile = async (
  playerId: string,
  payload: UpdatePlayerPayload
): Promise<PlayerProfileResponse> => {
  const { data } = await req(ENDPOINTS.PLAYERS.UPDATE_ME_PLAYER(playerId), {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
  return data as PlayerProfileResponse
}

/** POST /users/me/players/:playerId/image — avatar upload from the Edit modal. */
export const uploadMePlayerImage = async (
  playerId: string,
  file: File | string
): Promise<unknown> => {
  return req(ENDPOINTS.PLAYERS.UPLOAD_ME_PLAYER_IMAGE(playerId), {
    method: 'POST',
    body: file,
  })
}

/** DELETE /users/me/players/:playerId/image — avatar delete from the Edit modal. */
export const deleteMePlayerImage = async (
  playerId: string
): Promise<unknown> => {
  return req(ENDPOINTS.PLAYERS.DELETE_ME_PLAYER_IMAGE(playerId), {
    method: 'DELETE',
  })
}
